import { getDefaultValidPlacementSizes } from '@helpers/Creative';
import { confirm } from '@libs/alerts';
import { TYPE } from '@master/constants';

const WARNING = {
  width: false,
  height: false,
  message: null,
};

export default {
  subscribers: new Set(),

  valid_sizes: null,
  warnings: Object.assign(structuredClone(WARNING), {
    expanded: structuredClone(WARNING),
  }),

  subscribe(cb, vnode) {
    this.unsubscribe(cb, vnode);
    this.subscribers.add(cb);
    cb(this.data());
  },

  unsubscribe(cb, vnode) {
    if (vnode != null) {
      vnode.$once('hook:beforeDestroy', () => {
        this.subscribers.delete(cb);
      });
    }
  },

  reset() {
    this.warnings = Object.assign(structuredClone(WARNING), {
      expanded: structuredClone(WARNING),
    });
    this.dispatch();
  },

  dispatch() {
    for (let cb of this.subscribers) {
      if (cb != null) {
        cb(this.data());
      }
    }
  },

  data() {
    return {
      warning: this.warnings,
      has_warning:
        this.warnings.width ||
        this.warnings.height ||
        this.warnings.expanded.width ||
        this.warnings.expanded.height,
    };
  },

  getWarningMessage(expanded = false) {
    if (expanded) {
      this.warnings.expanded.message = null;

      const key =
        this.warnings?.expanded?.width && this.warnings?.expanded?.height
          ? 'width & height'
          : this.warnings?.expanded?.width
            ? 'width'
            : this.warnings?.expanded?.height
              ? 'height'
              : null;

      if (key == null) {
        return;
      }

      this.warnings.expanded.message = `Please define ${key} of the expanded creative (between ${this.valid_sizes?.width?.min} and ${this.valid_sizes?.width?.max} px)`;
    } else {
      this.warnings.message = null;

      const key =
        this.warnings?.width && this.warnings?.height
          ? 'width & height'
          : this.warnings?.width
            ? 'width'
            : this.warnings?.height
              ? 'height'
              : null;

      if (key == null) {
        return;
      }

      this.warnings.message = `Please define ${key} of the placement (between ${this.valid_sizes?.width?.min} and ${this.valid_sizes?.width?.max} px)`;
    }
  },

  validateCreativeSize(creative) {
    if (creative?.expanded != null) {
      const valid = this.isValidSize(creative, null);
      const valid_expanded = this.isValidSize(creative.expanded, null, true);
      return valid && valid_expanded;
    }

    return this.isValidSize(creative, null);
  },

  isValidSize(creative, key = 'width', expanded = false) {
    this.initValidSizes(creative);

    let is_valid = false;

    switch (key) {
      case 'width':
        is_valid = this.isValidWidth(creative, expanded);
        break;
      case 'height':
        is_valid = this.isValidHeight(creative, expanded);
        break;
      default:
        const valid_width = this.isValidWidth(creative, expanded);
        const valid_height = this.isValidHeight(creative, expanded);
        is_valid = valid_width && valid_height;
        break;
    }

    this.getWarningMessage(expanded);
    this.dispatch();

    return is_valid;
  },

  isValidWidth(creative, expanded = false) {
    const width = parseInt(creative?.width ?? 0, 10);
    const valid =
      width >= this.valid_sizes?.width?.min &&
      width <= this.valid_sizes?.width?.max;

    if (expanded) {
      this.warnings.expanded.width = !valid;
    } else {
      this.warnings.width = !valid;
    }

    return valid;
  },

  isValidHeight(creative, expanded = false) {
    const height = parseInt(creative?.height ?? 0, 10);
    const valid =
      height >= this.valid_sizes?.height?.min &&
      height <= this.valid_sizes?.height?.max;

    if (expanded) {
      this.warnings.expanded.height = !valid;
    } else {
      this.warnings.height = !valid;
    }

    return valid;
  },

  /**
   * @name confirmSizeChange
   * @param {*} creative full creative object
   * @param {{ width: string, height: string }} previous_values
   * @param {boolean} reimport_social
   * @returns
   */
  async confirmSizeChange(creative, previous_values, reimport_social = false) {
    const have_assets =
      creative?.assets &&
      Object.keys(creative.assets).filter(k => k !== 'splash').length > 0;
    const have_additional_assets =
      creative?.additional_assets &&
      Object.keys(creative.additional_assets).length > 0;

    // no need to show asset warning if no assets are found
    if (!have_assets && !have_additional_assets) return true;

    const width = previous_values?.width ?? creative.width;
    const height = previous_values?.height ?? creative.height;

    if (creative.width === width && creative.height === height) return true;

    const title = 'Are you sure you want to change the placement size?';
    let content =
      'Changing the size can result in your assets not being fully visible at their current size. Please preview the ad before publishing to ensure everything looks good.';
    let notice = null;

    const buttons = [
      { type: 'link-primary', label: 'Cancel', action: false },
      { type: 'primary', label: 'OK', action: true },
    ];

    if (reimport_social) {
      buttons.splice(1, 0, {
        type: 'primary',
        label: 'Reimport',
        outline: true,
        action: 'reimport',
      });
      notice =
        'Reimporting social will automatically re-crop and replace any asset';
      content += `\n\n${notice}`;
    }

    if (this.creative?.ext_banner_ids?.length > 0) {
      content = `<p>This creative is selected into a published <a href="/b/${this.creative.campaign_id}" target="_blank">buy media campaign</a>, so changing the size could affect your campaign setup and results (we have to recalculate the optimal price for this creative)</p>
      <p>${content}</p>`;

      if (notice) {
        content += `<p>${notice}</p>`;
      }
    }

    return await confirm(undefined, title, content, { buttons });
  },

  initValidSizes(creative) {
    let key = 'mobile';

    if (
      creative?.type === TYPE.VAST &&
      creative?.settings?.ctv_format === true
    ) {
      key = 'ctv_video';
    } else if (creative?.type === TYPE.VAST || creative?.type === TYPE.VPAID) {
      key = 'video';
    }

    this.valid_sizes = getDefaultValidPlacementSizes()[key];
    return this.valid_sizes;
  },
};
